import { useEffect, useState, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { cn } from '@/lib/utils'

const SPRING = { stiffness: 380, damping: 36, mass: 0.45 }
const INTERACTIVE = 'a, button, input, textarea, select'
const OFFSET_X = 16
const OFFSET_Y = 20

type SliderSwipePromptProps = {
  children: ReactNode
  className?: string
}

function isInteractiveTarget(target: EventTarget | null) {
  return Boolean((target as HTMLElement | null)?.closest?.(INTERACTIVE))
}

export default function SliderSwipePrompt({ children, className }: SliderSwipePromptProps) {
  const [canHover, setCanHover] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [visible, setVisible] = useState(false)
  const [overInteractive, setOverInteractive] = useState(false)
  const [dragging, setDragging] = useState(false)

  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, SPRING)
  const springY = useSpring(y, SPRING)
  const followX = useTransform(reducedMotion ? x : springX, (v) => v + OFFSET_X)
  const followY = useTransform(reducedMotion ? y : springY, (v) => v + OFFSET_Y)

  useEffect(() => {
    const hoverMq = window.matchMedia('(hover: hover) and (pointer: fine)')
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => {
      setCanHover(hoverMq.matches)
      setReducedMotion(motionMq.matches)
    }
    sync()
    hoverMq.addEventListener('change', sync)
    motionMq.addEventListener('change', sync)
    return () => {
      hoverMq.removeEventListener('change', sync)
      motionMq.removeEventListener('change', sync)
    }
  }, [])

  const show = canHover && visible && !overInteractive && !dragging

  const onPointerEnter = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return
    x.jump(e.clientX)
    y.jump(e.clientY)
    setOverInteractive(isInteractiveTarget(e.target))
    setVisible(true)
  }

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return
    x.set(e.clientX)
    y.set(e.clientY)
    setOverInteractive(isInteractiveTarget(e.target))
  }

  const onPointerLeave = () => {
    setVisible(false)
    setOverInteractive(false)
    setDragging(false)
  }

  return (
    <div
      className={cn('relative w-full', className)}
      onPointerEnter={onPointerEnter}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onPointerDown={() => setDragging(true)}
      onPointerUp={() => setDragging(false)}
      onPointerCancel={() => setDragging(false)}
    >
      {children}
      {canHover &&
        createPortal(
          <AnimatePresence>
            {show && (
              <motion.div
                key="slider-swipe-prompt"
                aria-hidden
                className="pointer-events-none fixed top-0 left-0 z-[80]"
                style={{ x: followX, y: followY }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0.12 : 0.2 }}
              >
                <motion.div
                  initial={reducedMotion ? false : { y: 4 }}
                  animate={{ y: 0 }}
                  exit={reducedMotion ? undefined : { y: 3 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-0.5 rounded-full bg-black/40 px-2 py-[3px] text-white/80 backdrop-blur-[6px]"
                >
                  <motion.span
                    className="flex opacity-70"
                    animate={reducedMotion ? undefined : { x: [0, -1.5, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-2.5 w-2.5">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </motion.span>
                  <span className="text-[9px] font-medium font-heading uppercase tracking-[0.16em]">
                    Swipe
                  </span>
                  <motion.span
                    className="flex opacity-70"
                    animate={reducedMotion ? undefined : { x: [0, 1.5, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-2.5 w-2.5">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </motion.span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  )
}
